import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { SearchApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationSearchEventSource } from '~shared/modules/application-event/constants';
import { Attribute, CategoryFilter, PromotionFilterChangedPayload } from '~app/modules/filters/models';
import { PriceFilterRange, PriceRangeLabel, RangeFilter, RangeFilters } from '~shared/models';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import {
  CategoryPageApplicationEvent
} from '~shared/modules/application-event/models/category-page-application-event.model';

@Injectable({
  providedIn: 'root'
})
export class FiltersApplicationEventService {
  private selectedCategoriesCount: number;
  private selectedAttributes: { [key: string]: number };

  constructor(private applicationEventService: ApplicationEventService,
              private productsListUrlService: ProductsListUrlService) {
  }

  updateQueryParams(params: Params): void {
    const {filter: {categories = [], options: {...rest} = {}} = {}} = params;

    this.selectedCategoriesCount = categories.length;
    this.selectedAttributes = {};
    Object.keys(rest).forEach(attributeId => {
      this.selectedAttributes[attributeId] = rest[attributeId].length;
    });
  }

  /* Category Methods */
  categoryFilterChanged(categoriesSlugs: CategoryFilter[]): void {
    const categoryRemoved = categoriesSlugs.length < this.selectedCategoriesCount;
    const currentSelectedContent = [...categoriesSlugs.map(a => a.slug)];
    this.fire('category', categoryRemoved
      ? ApplicationSearchEventSource.FilterUnselected
      : ApplicationSearchEventSource.Filter, currentSelectedContent.pop());
  }

  categoryTagRemoved(): void {
    this.fire('category', ApplicationSearchEventSource.FilterRemove);
  }

  CategoryReset(): void {
    this.fire('category', ApplicationSearchEventSource.FilterUnselected);
  }

  /*  Price Methods */
  priceFilterChanged(priceRanges: PriceFilterRange): void {
    this.fire('price', ApplicationSearchEventSource.Filter,
      priceRanges.type === PriceRangeLabel.min ?
        `${PriceRangeLabel.min}-${priceRanges.from}`
        : `${PriceRangeLabel.max}-${priceRanges.to}`);
  }

  priceTagRemoved(): void {
    this.fire('price', ApplicationSearchEventSource.FilterRemove);
  }

  /* Attribute Methods */
  attributeFilterChanged(attrs: { [key: string]: string }, content: string): void {
    let attributeRemoved = false;
    let updatedAttributeId;
    const attrsKeys: string[] = (attrs && Object.keys(attrs)) || [];
    const selectedAttributesKeys: string[] = (this.selectedAttributes && Object.keys(this.selectedAttributes)) || [];

    if (attrsKeys?.length !== selectedAttributesKeys?.length) {
      // New attribute filter has been applied or an attribute filter has been removed
      updatedAttributeId = attrsKeys.find(
        attribute => !this.selectedAttributes[attribute]
      );

      if (!updatedAttributeId) {
        // Removed attribute
        attributeRemoved = true;
        updatedAttributeId = selectedAttributesKeys?.find(
          (attribute: string) => attrs && !attrs[attribute]
        );
      }
    } else {
      // Attribute has been updated, where one attribute has many values,
      // ex: color: white is selected, and we select black
      // ex2: color: white and black are selected, and we remove black
      updatedAttributeId = attrsKeys.find(
        attribute => this.selectedAttributes[attribute] < Object.keys(attrs[attribute]).length
      );

      if (!updatedAttributeId) {
        attributeRemoved = true;
        updatedAttributeId = selectedAttributesKeys.find(
          (attribute) => {
            return !attrs[attribute] || Object.keys(attrs[attribute]).length < this.selectedAttributes[attribute];
          }
        );
      }
    }

    if (!!content) {
      this.fire(updatedAttributeId, attributeRemoved
        ? ApplicationSearchEventSource.FilterUnselected
        : ApplicationSearchEventSource.Filter, content);
    }
  }

  attributeFromModal(attributeName, OptionName: string): void {
    this.fire(attributeName, ApplicationSearchEventSource.Filter, OptionName);
  }

  attributeFilterRemoved(attribute: Attribute): void {
    this.fire(attribute.id, ApplicationSearchEventSource.FilterRemove);
  }

  attributeReset(attributeId: string): void {
    this.fire(attributeId, ApplicationSearchEventSource.FilterUnselected);
  }

  /* Promotion Methods */
  promotionFilterChanged({promotion}: PromotionFilterChangedPayload): void {
    // in case isHasPromotion = {} => that means we just removed the promotion filter
    const promotionRemoved = promotion !== true;
    this.fire('promotion', promotionRemoved
      ? ApplicationSearchEventSource.FilterUnselected
      : ApplicationSearchEventSource.Filter, 'promotion');
  }

  volumePricesFilterChanged({volumePrices}: PromotionFilterChangedPayload) {
    const volumePriceRemoved = volumePrices !== true;

    this.fire('volumePrices', volumePriceRemoved
      ? ApplicationSearchEventSource.FilterUnselected
      : ApplicationSearchEventSource.Filter, 'volumePrices');
  }

  rangeFilterChanged(ranges: RangeFilters): void {
    const rangeType = Object.keys(ranges)[0];
    const range: RangeFilter = ranges[rangeType];
    this.fire('optionRanges',
      ApplicationSearchEventSource.Filter,
      `${range?.type} - ${range?.minOrMaxLabel} changed: ${range?.from} - ${range?.to}`);
  }

  promotionTagRemoved(): void {
    this.fire('promotion', ApplicationSearchEventSource.FilterRemove);
  }

  volumePriceTagRemoved(): void {
    this.fire('volumePrices', ApplicationSearchEventSource.FilterRemove);
  }

  /* General Methods */
  clearAllTags(): void {
    this.fire('remove-all', ApplicationSearchEventSource.FilterRemoveAll);
  }

  /* private methods */
  private fire(filterName: string, filterSourceType: ApplicationSearchEventSource, content?: string): void {
    if (this.productsListUrlService.isCategoryPage()) {
      this.applicationEventService.publish(new CategoryPageApplicationEvent(
          filterName,
          filterSourceType,
          undefined,
          undefined,
          undefined,
          content
        )
      );
      return;
    }

    this.applicationEventService.publish(new SearchApplicationEvent(
        filterName,
        filterSourceType,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        content
      )
    );
  }
}
